/**
 * DEV-ONLY in-memory mock for the notes-pipeline API (Kith hackathon).
 *
 * The parallel branch owns the real endpoints; this file lets the UI be
 * exercised end-to-end in the browser before that merges. Flag defaults
 * OFF; flip it in a dev preview with EITHER:
 *   - URL param:      ?mockNotes=1
 *   - localStorage:   localStorage.setItem('kith-notes-mock', '1')
 * Production builds never enable it (import.meta.env.DEV gate).
 *
 * DELETION PLAN: when the real API merges, delete this file and collapse
 * the one ternary at the bottom of src/lib/notes.ts. Nothing else
 * imports this module.
 *
 * Demo data is tenant-neutral placeholder content (no real staff or
 * client names — product-framing discipline).
 */

import type {
  NoteCreateInput,
  NotesApi,
  NoteWire,
  NotesListResponse,
  ProposalListFilter,
  ProposalWire,
  ProposalsResponse,
} from './notes';
import { hasInvoiceLine } from './invoiceLinePresence';

export function notesMockEnabled(): boolean {
  if (!import.meta.env.DEV) return false;
  if (typeof window === 'undefined') return false;
  try {
    if (new URLSearchParams(window.location.search).get('mockNotes') === '1') return true;
    return window.localStorage.getItem('kith-notes-mock') === '1';
  } catch {
    return false;
  }
}

const PAGE_SIZE = 5;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

let nextId = 100;
function id(prefix: string): string {
  nextId += 1;
  return `${prefix}-${nextId}`;
}

export function createMockNotesApi(): NotesApi {
  const now = Date.now();
  const iso = (msAgo: number): string => new Date(now - msAgo).toISOString();

  const notes: NoteWire[] = [
    {
      // Content-sufficiency kickback demo: the pipeline judged this
      // note's content does not justify the billed time, so no line
      // was drafted and the author is asked to add substance or write
      // the line (the founder's calibration case: a bare "travel"
      // against 2 hours).
      id: 'note-4',
      occurred_at: iso(3600000),
      rate_band: 'travel',
      hours: 2,
      after_hours: false,
      raw_text: 'travel',
      invoice_summary_draft: null,
      invoice_summary_skip_reason: 'insufficient_content',
      status: 'proposals_ready',
      source: 'native',
      author: 'Avery CM',
      author_user_id: 'mock-avery-cm',
      posted_at: iso(3600000),
      voided_at: null,
      void_reason: null,
      superseded_by_note_id: null,
      billing_ready_at: null,
      kind: 'visit',
    },
    {
      id: 'note-3',
      occurred_at: iso(2 * 86400000),
      rate_band: 'care_management',
      hours: 1.5,
      after_hours: false,
      raw_text:
        'Home visit. Client in good spirits; reviewed the med list with the aide. ' +
        'Dr. R. increased metoprolol to 50mg twice daily after last week’s ' +
        'follow-up. Daughter asked about respite options for August – will pull ' +
        'two facility choices from the directory.',
      invoice_summary_draft: 'Home visit: medication review and family coordination (1.5h).',
      invoice_summary_skip_reason: null,
      status: 'posted',
      source: 'native',
      author: 'Avery CM',
      author_user_id: 'mock-avery-cm',
      posted_at: iso(2 * 86400000),
      voided_at: null,
      void_reason: null,
      superseded_by_note_id: null,
      billing_ready_at: null,
      kind: 'visit',
    },
    {
      id: 'note-2',
      occurred_at: iso(86400000),
      rate_band: 'admin',
      hours: 0.25,
      after_hours: false,
      raw_text: 'Faxed the updated POLST to the cardiology office; confirmed receipt.',
      invoice_summary_draft: 'Records coordination with cardiology (0.25h).',
      invoice_summary_skip_reason: null,
      status: 'posted',
      source: 'native',
      author: 'Avery CM',
      author_user_id: 'mock-avery-cm',
      posted_at: iso(86400000),
      voided_at: null,
      void_reason: null,
      superseded_by_note_id: null,
      billing_ready_at: null,
      kind: 'system',
    },
    {
      id: 'note-1',
      occurred_at: iso(4 * 3600000),
      rate_band: 'care_management',
      hours: 0.5,
      after_hours: true,
      raw_text:
        'Evening call from the son – client had a fall around 7pm, no injury, ' +
        'seen at St. Example ED and discharged the same night. Will schedule a ' +
        'PT re-evaluation and check the bathroom grab bars at Thursday’s visit.',
      invoice_summary_draft: 'After-hours family call re: fall and ED visit (0.5h).',
      invoice_summary_skip_reason: null,
      status: 'posted',
      source: 'native',
      author: 'Avery CM',
      author_user_id: 'mock-avery-cm',
      posted_at: iso(4 * 3600000),
      voided_at: null,
      void_reason: null,
      superseded_by_note_id: null,
      billing_ready_at: null,
      kind: 'family',
    },
  ];

  let proposals: ProposalWire[] = [
    {
      id: 'prop-seed-1',
      proposal_type: 'hospital_event',
      payload: { event: 'ED visit after a fall', facility: 'St. Example ED', disposition: 'discharged same night' },
      source_quote: 'client had a fall around 7pm, no injury, seen at St. Example ED and discharged the same night',
      confidence: 0.93,
      status: 'proposed',
    },
    {
      id: 'prop-seed-2',
      proposal_type: 'medication_change',
      payload: {
        name: 'metoprolol',
        dose: '50mg',
        schedule: 'twice daily',
        prescriber: 'Dr. R.',
        status: 'active',
      },
      source_quote: 'Dr. R. increased metoprolol to 50mg twice daily',
      confidence: 0.9,
      status: 'proposed',
    },
    {
      id: 'prop-seed-3',
      proposal_type: 'directive_change',
      payload: { directive: 'POLST', change: 'updated copy sent to cardiology' },
      source_quote: 'Faxed the updated POLST to the cardiology office',
      confidence: 0.62,
      status: 'proposed',
    },
  ];

  /** Canned proposal generation for a just-read note. Called
   * SYNCHRONOUSLY from the mock runExtraction (mirroring the real
   * contract: when the extract call resolves, proposals are committed). */
  function generateProposalsFor(note: NoteWire): void {
    const text = note.raw_text.toLowerCase();
    const generated: ProposalWire[] = [];
    if (/(\bmg\b|\bmed\b|medication|dose)/.test(text)) {
      generated.push({
        id: id('prop'),
        proposal_type: 'medication_change',
        payload: { name: 'as noted', dose: '', schedule: '', prescriber: '' },
        source_quote: note.raw_text.slice(0, 120),
        confidence: 0.86,
        status: 'proposed',
      });
    }
    if (/(hospital|\bed\b|emergency|admitted|discharge)/.test(text)) {
      generated.push({
        id: id('prop'),
        proposal_type: 'hospital_event',
        payload: { event: 'possible hospital contact', disposition: 'see note' },
        source_quote: note.raw_text.slice(0, 120),
        confidence: 0.88,
        status: 'proposed',
      });
    }
    // Always at least one: a follow-up task guess at lower confidence.
    generated.push({
      id: id('prop'),
      proposal_type: 'follow_up_task',
      payload: { task: 'Follow up on items from this note' },
      source_quote: note.raw_text.slice(0, 120),
      confidence: 0.55,
      status: 'proposed',
    });
    proposals = [...generated, ...proposals];
  }

  return {
    async createNote(_slug: string, _clientId: string, input: NoteCreateInput): Promise<NoteWire> {
      await delay(400);
      const note: NoteWire = {
        id: id('note'),
        occurred_at: input.occurred_at,
        rate_band: input.rate_band,
        // Mirror toNoteWire: a null (blank) hours reads back as 0 on the wire.
        hours: input.hours ?? 0,
        after_hours: input.after_hours,
        raw_text: input.raw_text,
        // A line filed with the note (a SPICE visit) rides the create; a
        // create is never an edit.
        invoice_summary_draft: input.invoice_summary_draft ?? null,
        invoice_summary_skip_reason: null,
        status: 'posted',
        source: 'native',
        author: 'You',
        author_user_id: 'mock-you',
        posted_at: new Date().toISOString(),
        voided_at: null,
        void_reason: null,
        superseded_by_note_id: null,
        billing_ready_at: null,
        kind: input.kind ?? 'visit',
        summary_edited: false,
        voided_by: null,
        last_edited: null,
      };
      notes.unshift(note);
      // No proposal scheduling here — the composer drives extraction via
      // runExtraction after the post (the real client-driven contract).
      return note;
    },

    async listNotes(
      _slug: string,
      _clientId: string,
      page: number,
      opts?: { includeRemoved?: boolean; q?: string },
    ): Promise<NotesListResponse> {
      await delay(250);
      const words = (opts?.q ?? '').toLowerCase().split(/\s+/).filter((w) => w.length > 0);
      const visible = (opts?.includeRemoved ? notes : notes.filter((n) => n.voided_at === null)).filter((n) =>
        words.every((w) => `${n.raw_text} ${n.invoice_summary_draft ?? ''}`.toLowerCase().includes(w)),
      );
      const start = (page - 1) * PAGE_SIZE;
      return {
        notes: visible.slice(start, start + PAGE_SIZE),
        page,
        total: visible.length,
        page_size: PAGE_SIZE,
      };
    },

    async listProposals(
      _slug?: string,
      _clientId?: string,
      filter: ProposalListFilter = 'open',
    ): Promise<ProposalsResponse> {
      await delay(250);
      // 'open' is both unresolved states (notes.ts OPEN_PROPOSAL_STATUSES;
      // named here, not imported, since notes.ts imports this module).
      const statuses: readonly string[] = filter === 'open' ? ['proposed', 'held_for_review'] : [filter];
      return { proposals: proposals.filter((p) => statuses.includes(p.status)) };
    },

    async resolveProposal(
      _slug: string,
      proposalId: string,
      input: { action: 'confirmed' | 'dismissed'; payload?: Record<string, unknown> },
    ): Promise<ProposalWire> {
      await delay(300);
      const found = proposals.find((p) => p.id === proposalId);
      if (!found) {
        throw new Error(`mock: no proposal ${proposalId}`);
      }
      found.status = input.action;
      if (input.payload) found.payload = input.payload;
      proposals = proposals.filter((p) => p.id !== proposalId);
      return found;
    },

    async updateNoteSummary(
      _slug: string,
      noteId: string,
      invoiceSummaryDraft: string,
    ): Promise<NoteWire> {
      await delay(200);
      const note = notes.find((n) => n.id === noteId);
      if (!note) throw new Error(`mock: no note ${noteId}`);
      note.invoice_summary_draft = invoiceSummaryDraft;
      // A human line supersedes the sufficiency skip (server behavior).
      note.invoice_summary_skip_reason = null;
      note.summary_edited = true;
      note.billing_ready_at = null;
      note.last_edited = { by: 'You', at: new Date().toISOString() };
      return note;
    },

    async setBillingReady(_slug: string, noteId: string, ready: boolean): Promise<void> {
      await delay(150);
      const note = notes.find((n) => n.id === noteId);
      if (!note) throw new Error(`mock: no note ${noteId}`);
      // The server's eligibility rule: read, not sent back, and holding a
      // line (a line that is only "No charge" is no line).
      if (
        ready &&
        (note.status !== 'proposals_ready' ||
          note.invoice_summary_skip_reason !== null ||
          !hasInvoiceLine(note.invoice_summary_draft))
      ) {
        throw new Error(`mock: note ${noteId} is not eligible to mark ready`);
      }
      note.billing_ready_at = ready ? new Date().toISOString() : null;
      // Editing (elsewhere) clears this; the mock mirrors the server flip.
    },

    async removeNote(_slug: string, noteId: string, reason?: string) {
      await delay(250);
      const note = notes.find((n) => n.id === noteId);
      if (!note) throw new Error(`mock: no note ${noteId}`);
      note.voided_at = new Date().toISOString();
      note.void_reason = reason && reason.trim() !== '' ? reason.trim() : null;
      note.voided_by = 'You';
      return { note_id: noteId, dismissed_proposal_count: 0, confirmed_update_count: 0 };
    },

    async supersedeNote(_slug: string, noteId: string, input: NoteCreateInput) {
      await delay(350);
      const old = notes.find((n) => n.id === noteId);
      if (!old) throw new Error(`mock: no note ${noteId}`);
      const replacement: NoteWire = {
        id: id('note'),
        occurred_at: input.occurred_at,
        rate_band: input.rate_band,
        // Mirror toNoteWire: a null (blank) hours reads back as 0 on the wire.
        hours: input.hours ?? 0,
        after_hours: input.after_hours,
        raw_text: input.raw_text,
        invoice_summary_draft: null,
        invoice_summary_skip_reason: null,
        status: 'posted',
        // Mirror the server: a replacement copies the source of the note
        // it replaces.
        source: old.source,
        // The replacement keeps the old note's author (an Edit by a
        // moderating seat leaves the note with the person who wrote it).
        author: old.author,
        author_user_id: old.author_user_id,
        posted_at: new Date().toISOString(),
        voided_at: null,
        void_reason: null,
        superseded_by_note_id: null,
        billing_ready_at: null,
        kind: input.kind ?? old.kind ?? 'visit',
        summary_edited: false,
        voided_by: null,
        last_edited: { by: 'You', at: new Date().toISOString() },
      };
      notes.unshift(replacement);
      old.voided_at = new Date().toISOString();
      old.void_reason = 'superseded';
      old.superseded_by_note_id = replacement.id;
      old.voided_by = 'You';
      return { note: replacement, superseded_note_id: noteId };
    },

    async runExtraction(_slug: string, noteId: string): Promise<{ note_id: string; status: string }> {
      // Simulated synchronous extraction (the real route's contract): a
      // think-time delay, then proposals are committed BEFORE resolving.
      await delay(1800);
      const note = notes.find((n) => n.id === noteId);
      if (note) {
        note.status = 'proposals_ready';
        generateProposalsFor(note);
      }
      return { note_id: noteId, status: 'proposals_ready' };
    },
  };
}
